import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import type TokenResponseCode from "owa-service/lib/contract/TokenResponseCode";

export const REDIRECT_URI_SUFFIX = "/mail/oauthRedirect.html";

export const Mail_VDIR = "/mail/";

// This is to make sure that the Consumers are redirected to msn.com after logout
export const POST_LOGOUT_REDIRECT_URI_CONSUMER = "https://www.msn.com/";

export const POST_LOGOUT_REDIRECT_URI_CONSUMER_SUFFIX = "/mail/ConsumerSignout.html";

// Here https://www.microsoft.com/ is not a registered reply url.
// This is a temporary fix to make sure that the Enterprise users stay at the logout page
// after logout. For enterprise scenario we don't want them to be redirected anywhere.
// But not setting the postlogoutredirecturi at all in Enterprise scenarios,
// somehow is picking up https://outlook-sdf.office.com/mail as the postlogoutredirecturi.
// But the problem with taking the user to /mail is it invokes the login flow.
// The long term fix here is that the MSAL team adds support for a param in the logout request to ensure that
// the user is not redirected any where and stays on the logout page.
export const POST_LOGOUT_REDIRECT_URI_ENTERPRISE = "https://www.microsoft.com/";

export const POST_LOGOUT_REDIRECT_URI_SHARED_ABT = "/mail/ABTSignout.html";

export const AUTHORITY_COMMON = "https://login.microsoftonline.com/common/";
export const AUTHORITY_ORGANIZATIONS = "https://login.microsoftonline.com/organizations/";
export const AUTHORITY_CONSUMERS = "https://login.microsoftonline.com/consumers/";
export const MSA_DEFAULT_SCOPE = "service::outlook.office.com::MBI_SSL";
export const OWA_DEFAULT_SCOPE = "https://outlook.office.com/.default";
export const EXCHANGE_CONSUMER_TENANT_ID = "84df9e7f-e9f6-40af-b435-aaaaaaaaaaaa";
export const MICROSOFT_CONSUMER_TENANT_ID = "9188040d-6c67-4c5b-b112-36a304b66dad";
export const PENDING_REDIRECT_KEY = "olk-msal-redirect";

export const TOKEN_RESPONSE_UNSUPPORTED_ACCOUNT_TYPE: TokenResponse = {
    TokenResultCode: 2,
    SubErrorCode: "unsupported_account_type",
};

export const TOKEN_RESPONSE_MSAL_NOT_ENABLED: TokenResponse = {
    TokenResultCode: 2,
    SubErrorCode: "msal_not_enabled",
};

export const OUTLOOK_COBRAND_ID = "ab0455a0-8d03-46b9-b18b-df2f57b9e44c";

/**
 * identifierUris registered to EXO (00000002-0000-0ff1-ce00-000000000000).
 */
export const EXO_RESOURCE_IDENTIFIER_URIS: RegExp[] = [
    /^https:\/\/outlook(-[st]df)?\.office(365)?\.com\/?$/i,
];

/**
 * Contains valid Outlook enterprise domains supported by MSAL. These URLs
 * should be registered as redirectUris, otherwise redirect errors will occur.
 */
export const OWA_MSAL_ENTERPRISE_PUBLIC_ORIGINS: RegExp[] = [
    /^https:\/\/outlook(-[st]df)?\.office(365)?\.com$/i,
    /^https:\/\/(sdf\.)?outlook\.cloud\.microsoft$/i,
];

/**
 * Contains valid Places domains supported by MSAL. These URLs should be
 * registered as redirectUris, otherwise redirect errors will occur.
 */
export const PLACES_MSAL_ENTERPRISE_PUBLIC_ORIGINS: RegExp[] = [
    /^https:\/\/places(-sdf)?\.cloud\.microsoft$/i,
];

/**
 * Contains valid enterprise domains supported by MSAL. These URLs should be
 * registered as redirectUris, otherwise redirect errors will occur.
 */
export const MSAL_ENTERPRISE_PUBLIC_ORIGINS: RegExp[] = [
    ...OWA_MSAL_ENTERPRISE_PUBLIC_ORIGINS,
    ...PLACES_MSAL_ENTERPRISE_PUBLIC_ORIGINS,
];

export const OWA_BASE_PATH = "https://outlook.office.com";

export const CONSUMER_OWA_BASE_PATH = "https://outlook.live.com";

//MSA retriable server error codes
export const msa_server_error_1003030 = "1003030";
export const msa_server_error_70012 = "70012";
export const msa_server_error_90032 = "90032";

/**
 * Language mappings for marketing landing page (matches server-side LandingPageUtils.cs)
 */
export const marketingLandingPageMapping: Record<string, string> = {
    af: "en-za",
    am: "en",
    ar: "ar",
    "as-in": "en-in",
    "az-latn-az": "en",
    "be-by": "ru-ru",
    bg: "bg-bg",
    "bn-bd": "en",
    "bn-in": "en-in",
    "bs-latn-ba": "en",
    ca: "es-es",
    "ca-es-valencia": "es-es",
    cs: "cs-cz",
    cy: "en-gb",
    da: "da-dk",
    de: "de-de",
    el: "el-gr",
    en: "en-us",
    "en-gb": "en-gb",
    es: "es-es",
    "es-mx": "es-mx",
    et: "et-ee",
    eu: "es-es",
    fa: "en",
    fi: "fi-fi",
    fil: "en",
    fr: "fr-fr",
    "fr-ca": "fr-ca",
    "ga-ie": "en-ie",
    "gd-gb": "en-gb",
    gl: "es-es",
    gu: "en-in",
    "ha-latn-ng": "en",
    he: "he-il",
    "hi-in": "en-in",
    hr: "hr-hr",
    hu: "hu-hu",
    hy: "en",
    id: "id-id",
    is: "en",
    it: "it-it",
    ja: "ja-jp",
    ka: "en",
    kk: "ru-ru",
    "km-kh": "en",
    kn: "en-in",
    ko: "ko-kr",
    kok: "en-in",
    ky: "ru-ru",
    "lb-lu": "fr-be",
    "lo-la": "en",
    lt: "lt-lt",
    lv: "lv-lv",
    "mi-nz": "en-nz",
    mk: "en",
    ml: "en-in",
    "mn-mn": "en",
    mr: "en-in",
    ms: "en-my",
    "mt-mt": "en",
    "ne-np": "en",
    nl: "nl-nl",
    "nn-no": "nb-no",
    no: "nb-no",
    or: "en-in",
    "pa-in": "en-in",
    pl: "pl-pl",
    prs: "en",
    pt: "pt-pt",
    "pt-br": "pt-br",
    "pt-pt": "pt-pt",
    "quz-pe": "es",
    ro: "ro-ro",
    ru: "ru-ru",
    "sd-arab-pk": "en",
    "si-lk": "en",
    sk: "sk-sk",
    sl: "sl-si",
    sq: "en",
    sr: "sr-latn-rs",
    "sr-cyrl": "sr-latn-rs",
    "sr-cyrl-ba": "en",
    sv: "sv-se",
    sw: "en",
    ta: "en-in",
    te: "en-in",
    th: "th-th",
    "tk-tm": "ru-ru",
    tr: "tr-tr",
    tt: "ru-ru",
    "ug-cn": "zh-cn",
    uk: "uk-ua",
    ur: "en",
    uz: "en",
    vi: "vi-vn",
    "zh-hans": "zh-cn",
    "zh-hant": "zh-hk",
    // Fallback mappings
    as: "en",
    az: "en",
    be: "ru-ru",
    bs: "en",
    ga: "en-ie",
    gd: "en-gb",
    ha: "en",
    km: "en",
    lb: "fr-be",
    lo: "en",
    mi: "en-nz",
    mn: "en",
    mt: "en",
    ne: "en",
    nb: "nb-no",
    nn: "nb-no",
    pa: "en",
    "pa-arab": "en",
    quz: "es",
    "sd-arab": "en",
    "sd-deva": "en-gb",
    sd: "en-gb",
    si: "en",
    tk: "ru-ru",
    ug: "zh-cn",
    "zh-cn": "zh-cn",
    "zh-sg": "zh-cn",
    "zh-hk": "zh-hk",
    "zh-mo": "zh-hk",
    "zh-tw": "zh-tw",
    zh: "zh-cn",
};

export const DEFAULT_LANGUAGE = "en-us";
export const LANGUAGE_TAGS_MAX_NUMBER = 5;

/**
 * Default marketing landing page URL template
 * Format: https://www.microsoft.com/{0}/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook?redirectTo={1}&sdf={2}
 * {0} = language locale (e.g., 'en-us', 'de-de')
 * {1} = encoded redirectTo path
 * {2} = SDF parameter ('0' or '1')
 */
export const DEFAULT_MARKETING_LANDING_PAGE_URL_TEMPLATE = "https://www.microsoft.com/{0}/microsoft-365/outlook/email-and-calendar-software-microsoft-outlook?redirectTo={1}&sdf={2}";

export const JWE_TOKEN_DEFAULT_SCOPE = "api://6bc89243-be1c-423a-99ec-b9a45b8b568b/.default";
